import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Download, Printer } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { buttonVariants } from "@/shared/components/ui/button-variants";
import { VenueStatus } from "./types";
import { useDownloadVenueQr } from "./use-venue-qr";

interface VenueQrActionsProps {
  readonly venue: {
    readonly id: string;
    readonly name: string;
    readonly status: VenueStatus;
  };
}

/**
 * Download / Print the venue's check-in QR (admin + support). Rendered only
 * for ACTIVE venues: the QR token is issued on approval and cleared on
 * suspension, so any other status has nothing to print (the API answers 400).
 */
export function VenueQrActions({ venue }: VenueQrActionsProps) {
  const { t } = useTranslation();
  const download = useDownloadVenueQr();
  const [failed, setFailed] = useState(false);

  if (venue.status !== VenueStatus.ACTIVE) return null;

  const onDownload = async () => {
    setFailed(false);
    try {
      await download.mutateAsync({ id: venue.id, name: venue.name });
    } catch {
      // The mutation already carries the ApiError; the copy is the same for
      // every failure (400 no token, 404, network) — "try again".
      setFailed(true);
    }
  };

  return (
    <div className="flex flex-col items-end gap-2" data-testid="venue-qr-actions">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={onDownload}
          disabled={download.isPending}
          data-testid="venue-qr-download"
        >
          <Download className="mr-2 h-4 w-4" />
          {download.isPending
            ? t("venuesDirectory.qr.downloading")
            : t("venuesDirectory.qr.downloadButton")}
        </Button>
        <Link
          to={`/venues-directory/${encodeURIComponent(venue.id)}/qr`}
          className={buttonVariants({ variant: "default" })}
          data-testid="venue-qr-print"
        >
          <Printer className="mr-2 h-4 w-4" />
          {t("venuesDirectory.qr.printButton")}
        </Link>
      </div>
      {failed && (
        <p className="font-sans text-sm text-destructive" data-testid="venue-qr-error">
          {t("venuesDirectory.qr.downloadError")}
        </p>
      )}
    </div>
  );
}
